import styled from "styled-components";
import { colors, types } from "../../../libs/themes";

interface MediaCardWrapperProps {
    customHeight?: string;
    customBorderRadius?: string;
}

interface InfoRowProps {
    alignStart?: boolean;
    forceCenter?: boolean;
}

export const MediaPoster = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;

export const MediaPosterLink = styled.a`
    @media (hover: hover) {
        &:hover {
            cursor: pointer;
            background-color: ${colors.background.halfBlack};
        }
    }
`;

export const MediaPosterArt = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
`;

export const BlurredBackground = styled.img`
    position: absolute;
    inset: 0;
    width: 110%;
    height: 110%;
    top: -5%;
    left: -5%;
    object-fit: cover;
    filter: blur(24px);
    transform: scale(1);
    z-index: 0;
`;

export const MediaPosterImage = styled.img`
    position: relative;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: 50% 50%;
    border-radius: var(--video-card-border-radius);
    transition: all 0.3s ease-out;
    z-index: 1;

    @media (min-width: 768px) {
        &:not(.is-mobile) {
            border-radius: var(--video-card-border-radius);
        }
    }
`;

export const MediaAncillary = styled.article`
    position: relative;
    @media (min-width: 768px) {
        &:not(.is-mobile) {
            max-height: 0;
            opacity: 0;
            position: absolute;
            top: 100%;
        }
    }
`;

export const MediaMetadata = styled.section``;

export const MediaHighlight = styled.span``;

export const MediaSynopsis = styled.div``;

export const MediaGenre = styled.div``;

export const MediaCrew = styled.div``;

export const MediaRating = styled.div``;

export const MediaCard = styled.article``;

export const TrailerOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 20px;
    z-index: 2;
    pointer-events: auto;
    background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0.16) 0%,
        rgba(0, 0, 0, 0.42) 100%
    );
    backdrop-filter: blur(1px);
    transition:
        background 0.4s ease,
        backdrop-filter 0.4s ease,
        box-shadow 0.4s ease;

    @media (max-width: 768px) {
        padding: 12px;
        justify-content: flex-start;
        align-items: flex-start;
    }
`;

export const MediaCardWrapper = styled.section.withConfig({
    shouldForwardProp: (prop) => !["customHeight", "customBorderRadius"].includes(prop),
})<MediaCardWrapperProps>`
    display: flex;
    flex-flow: column nowrap;
    position: relative;
    will-change: transform;
    transition: all 0.3s ease-out;
    box-shadow: ${colors.shadow.cardLight};
    border: 1px solid ${({ theme }) => theme.cardSeparator};
    overflow: hidden;
    cursor: pointer;
    border-radius: ${({ customBorderRadius }) => customBorderRadius || `24px`};
    height: ${({ customHeight }) => customHeight || "720px"};

    &:hover ${TrailerOverlay} {
        background: linear-gradient(
            180deg,
            rgba(0, 0, 0, 0.24) 0%,
            rgba(0, 0, 0, 0.56) 100%
        );
        backdrop-filter: blur(5px);
        box-shadow: ${colors.shadow.hoverOverlay};
    }

    @media (min-width: 768px) {
        &:not(.is-mobile) {
            height: ${({ customHeight }) => customHeight || "720px"};
            display: block;
        }
    }
`;

export const MetaContent = styled.div`
    position: absolute;
    top: 0%;
    left: 0%;
    width: 100%;
    height: 100%;
    padding: 16px;
    color: ${colors.text.white};
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.65);
    z-index: 9999;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: center;
    text-align: center;
    justify-content: center;
    cursor: context-menu;
    background: transparent;
`;

export const InfoRow = styled.div<InfoRowProps>`
    display: flex;
    align-items: center;
    justify-content: ${(props) => (props.alignStart ? "flex-start" : "")};
    flex-wrap: nowrap;
    gap: 20px;
    width: 100%;
    overflow: auto;
    overflow: scroll;
    scrollbar-width: none;
    -ms-overflow-style: none;
`;

export const InfoRowSmallSperated = styled.div.withConfig({
    shouldForwardProp: (prop) => !["forceCenter", "alignStart"].includes(prop),
})<InfoRowProps>`
    display: flex;
    align-items: center;
    justify-content: ${(props) => (props.alignStart ? "" : "")};
    flex-wrap: nowrap;
    gap: 20px;
    width: 100%;
    overflow: auto;
    overflow: scroll;
    scrollbar-width: none;
    -ms-overflow-style: none;

    @media (max-width: 1024px) {
        justify-content: ${(props) =>
            props.forceCenter
                ? "center"
                : props.alignStart
                  ? ""
                  : "flex-start"};
    }
`;

export const InfoRowSmall = styled.div.withConfig({
    shouldForwardProp: (prop) => !["forceCenter", "alignStart"].includes(prop),
})<InfoRowProps>`
    display: flex;
    align-items: center;
    justify-content: ${(props) => (props.alignStart ? "" : "center")};
    flex-wrap: nowrap;
    gap: 20px;
    width: 100%;
    overflow: auto;
    overflow: scroll;
    scrollbar-width: none;
    -ms-overflow-style: none;

    @media (max-width: 1024px) {
        justify-content: ${(props) =>
            props.forceCenter
                ? "center"
                : props.alignStart
                  ? ""
                  : "flex-start"};
    }
`;

export const InfoItem = styled.div`
    display: flex;
    align-items: center;
    white-space: nowrap;
    color: inherit;
    svg {
        margin-right: 5px;
        color: inherit;
    }
    span {
        ${types.headers.small}
        color: inherit;
    }
    @media (max-width: 768px) {
        ${types.body.normal}
    }
`;

export const MediaTextWrapperNewInfoItem = styled.div`
    max-width: 460px;
`;

export const MediaTextWrapper = styled.div`
    max-width: 560px;
`;

export const MediaTitle = styled.div`
    ${types.headers.main42}
    line-height: normal;
    color: inherit;
    margin-bottom: 10px;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 900px) {
        font-size: 32px;
        ${types.body.title}
        font-weight: 600;
        text-align: center;
    }
`;

export const MediaDescription = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "limitLines",
})<{ limitLines?: boolean }>`
  ${(props) =>
    props.limitLines
      ? `
        white-space: normal;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      `
      : `
        ${types.body.mediumSubtitle}
        line-height: 24px;
        color: inherit;
        white-space: normal;
        word-wrap: break-word;

        @media (max-width: 900px) {
          ${types.body.label}
          text-align: center;
        }
      `}
`;

export const GenrePills = styled.div`
    display: flex;
    gap: 10px;

    @media (max-width: 768px) {
        justify-content: center;
        gap: 6px;
    }
`;

export const Genre = styled.div`
    ${types.headers.small}
    padding: 2px 12px;
    border: 1px solid currentColor;
    border-radius: 24px;
    color: inherit;
    height: 28px;
    display: flex;
    align-items: center;

    @media screen and (max-width: 1024px) and (min-width: 768px) and (orientation: portrait) {
        ${types.body.normal}
        padding: 2px 8px;
        height: 28px;
    }

    @media (max-width: 900px) {
        ${types.body.normal}
        padding: 2px 10px;
        height: 28px;
    }
`;

export const TrailerButton = styled.button`
    ${types.headers.small}
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: ${({ theme }) => theme.watchButtonBg};
    color: ${({ theme }) => theme.watchButtonColor};
    border: 1px solid transparent;
    padding: 10px 24px 10px 32px;
    cursor: pointer;
    transition: background-color 0.3s;

    &:hover {
        background-color: ${({ theme }) => theme.watchButtonHover};
        cursor: pointer;
    }

    &:focus {
        outline: none;
        border: none;
    }

    svg {
        margin-top: -1px;
    }
`;

export const NewMetaContent = styled.div`
    position: absolute;
    top: 56px;
    left: 47px;
    padding: 16px;
    color: ${colors.text.white};
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.65);
    z-index: 9999;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
    cursor: context-menu;
    align-items: flex-start;

    @media (max-width: 900px) {
        top: 24px;
        left: 16px;
        padding: 12px;
        gap: 12px;
    }
`;

export const NewInfoRow = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;

    @media (max-width: 900px) {
        justify-content: center;
        gap: 2px;
        flex-wrap: wrap;
    }
`;

export const Divider = styled.span`
    ${types.headers.itemMedium}
    display: flex;
    align-items: center;
    justify-content: center;
    color: inherit;
    font-weight: 500;
    height: 100%;
`;

export const NewInfoItem = styled.div`
    ${types.body.label}
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
    color: inherit;
    svg {
        color: inherit;
    }
    @media (max-width: 768px) {
        ${types.body.normal}
    }
`;

export const RedBadge = styled.span`
    ${types.body.normal}
    background-color: ${colors.text.lightGray};
    color: inherit;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: bold;
    @media (max-width: 768px) {
        ${types.body.small}
    }
`;
export const Wrapper = styled.div`
    width: 1150px;
    overflow: hidden;
`;

export const Title = styled.div`
    ${types.headers.main42}
    line-height: normal;
    color: inherit;
    margin: 0;
    text-align: left;
    word-wrap: break-word;
    word-break: break-word;

    @media screen and (max-width: 1024px) and (min-width: 768px) and (orientation: portrait) {
        ${types.headers.main30}
    }

    @media (max-width: 900px) {
        ${types.headers.itemMedium}
    }

    @media (max-width: 600px) {
        ${types.body.smallTitle}
        text-align: center;
    }
`;

export const Description = styled.div`
    ${types.body.mediumSubtitle}
    line-height: 24px;
    color: inherit;
    margin: 0;
    text-align: left;
    word-wrap: break-word;
    word-break: break-word;
    white-space: normal;

    @media (max-width: 900px) {
        ${types.body.label}
    }

    @media (max-width: 600px) {
        ${types.body.normal}
        text-align: center;
    }
`;

export const WatchNowButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: ${({ theme }) => theme.watchButtonBg};
    color: ${({ theme }) => theme.watchButtonColor};
    border: 1px solid transparent;
    padding: 10px 24px 10px 32px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.3s;
    text-align: left;

    &:hover {
        background-color: ${({ theme }) => theme.watchButtonHover};
        cursor: pointer;
    }
    @media (max-width: 768px) {
        ${types.body.normal}
        width: 100%;
        justify-content: center;
        padding: 10px 16px;
    }
`;


export const BigCardBottomProgressBar = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 10px;
    background-color: #ff0000;
`;
